/**
 * usePayrollUIState - 급여 관리 UI 상태 관리 Hook
 *
 * payroll-management.tsx의 UI 관련 상태를 분리
 * Phase 2 Step 2: UI 상태 Hook 분리
 *
 * @description
 * - 모달 표시 상태
 * - 비교 모드 토글
 * - IME 입력 상태
 * - 임시 입력값 관리
 */

import { useState, useCallback } from 'react';

// UI State Types
export interface PayrollUIState {
  /** 지난달 비교 표시 여부 */
  showComparisonWithPreviousMonth: boolean;
  /** 급여 확인 경고 모달 표시 여부 */
  showPayrollWarningModal: boolean;
  /** 대기 중인 급여 데이터 키 (모달 확인용) */
  pendingPayrollDataKey: string;
  /** 표시 중인 급여 데이터 키 Set */
  visiblePayrollData: Set<string>;
  /** 한국어 IME 입력 중 상태 (필드ID: 입력중여부) */
  isComposing: Record<string, boolean>;
  /** 임시 이름 입력값 (필드ID: 값) */
  tempNameValues: Record<string, string>;
}

export interface PayrollUIActions {
  // 비교 모드
  setShowComparisonWithPreviousMonth: (show: boolean) => void;
  toggleComparisonWithPreviousMonth: () => void;

  // 모달 제어
  setShowPayrollWarningModal: (show: boolean) => void;
  setPendingPayrollDataKey: (key: string) => void;
  openPayrollWarningModal: (dataKey: string) => void;
  closePayrollWarningModal: () => void;

  // 표시 데이터 제어
  setVisiblePayrollData: React.Dispatch<React.SetStateAction<Set<string>>>;
  addVisiblePayrollData: (key: string) => void;
  removeVisiblePayrollData: (key: string) => void;
  toggleVisiblePayrollData: (key: string) => void;
  clearVisiblePayrollData: () => void;

  // IME 입력 제어
  setIsComposing: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setComposingState: (fieldId: string, composing: boolean) => void;

  // 임시값 제어
  setTempNameValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setTempNameValue: (fieldId: string, value: string) => void;
  clearTempNameValue: (fieldId: string) => void;
}

export type UsePayrollUIStateReturn = PayrollUIState & PayrollUIActions;

/**
 * 급여 관리 UI 상태 Hook
 */
export function usePayrollUIState(): UsePayrollUIStateReturn {
  // === State Declarations ===

  // 지난달 데이터 표시 여부
  const [showComparisonWithPreviousMonth, setShowComparisonWithPreviousMonth] = useState<boolean>(false);

  // 급여 관리 확인 버튼 관련 상태
  const [showPayrollWarningModal, setShowPayrollWarningModal] = useState<boolean>(false);
  const [pendingPayrollDataKey, setPendingPayrollDataKey] = useState<string>('');
  const [visiblePayrollData, setVisiblePayrollData] = useState<Set<string>>(new Set());

  // 한국어 입력 상태 관리
  const [isComposing, setIsComposing] = useState<Record<string, boolean>>({});

  // 임시 입력값 저장
  const [tempNameValues, setTempNameValues] = useState<Record<string, string>>({});

  // === Action Implementations ===

  // 비교 모드 토글
  const toggleComparisonWithPreviousMonth = useCallback(() => {
    setShowComparisonWithPreviousMonth(prev => !prev);
  }, []);

  // 모달 열기 (데이터 키와 함께)
  const openPayrollWarningModal = useCallback((dataKey: string) => {
    setPendingPayrollDataKey(dataKey);
    setShowPayrollWarningModal(true);
  }, []);

  // 모달 닫기 (상태 초기화)
  const closePayrollWarningModal = useCallback(() => {
    setShowPayrollWarningModal(false);
    setPendingPayrollDataKey('');
  }, []);

  // 표시 데이터 추가
  const addVisiblePayrollData = useCallback((key: string) => {
    setVisiblePayrollData(prev => new Set([...prev, key]));
  }, []);

  // 표시 데이터 제거
  const removeVisiblePayrollData = useCallback((key: string) => {
    setVisiblePayrollData(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
  }, []);

  // 표시 데이터 토글
  const toggleVisiblePayrollData = useCallback((key: string) => {
    setVisiblePayrollData(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  // 표시 데이터 전체 초기화
  const clearVisiblePayrollData = useCallback(() => {
    setVisiblePayrollData(new Set());
  }, []);

  // IME 입력 상태 설정
  const setComposingState = useCallback((fieldId: string, composing: boolean) => {
    setIsComposing(prev => ({
      ...prev,
      [fieldId]: composing
    }));
  }, []);

  // 임시 이름값 설정
  const setTempNameValue = useCallback((fieldId: string, value: string) => {
    setTempNameValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  }, []);

  // 임시 이름값 제거
  const clearTempNameValue = useCallback((fieldId: string) => {
    setTempNameValues(prev => {
      const newValues = { ...prev };
      delete newValues[fieldId];
      return newValues;
    });
  }, []);

  // === Return ===
  return {
    // State
    showComparisonWithPreviousMonth,
    showPayrollWarningModal,
    pendingPayrollDataKey,
    visiblePayrollData,
    isComposing,
    tempNameValues,

    // Actions - 비교 모드
    setShowComparisonWithPreviousMonth,
    toggleComparisonWithPreviousMonth,

    // Actions - 모달
    setShowPayrollWarningModal,
    setPendingPayrollDataKey,
    openPayrollWarningModal,
    closePayrollWarningModal,

    // Actions - 표시 데이터
    setVisiblePayrollData,
    addVisiblePayrollData,
    removeVisiblePayrollData,
    toggleVisiblePayrollData,
    clearVisiblePayrollData,

    // Actions - IME
    setIsComposing,
    setComposingState,

    // Actions - 임시값
    setTempNameValues,
    setTempNameValue,
    clearTempNameValue,
  };
}

export default usePayrollUIState;
